import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Plus, Trash2, Loader2, Link2, Building2, Edit } from "lucide-react";
import { format } from "date-fns";
import type { Account, AccountContact, Contact, AccountPeople } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AccountFormModal } from "@/components/account-form-modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { AccountSelect } from "@/components/form-fields/account-select";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";

interface LinkedAccountWithDetails extends AccountContact {
  account?: Account;
}

interface AccountPeopleWithDetails extends AccountPeople {
  account?: Account | null;
}

type CombinedAccountLink = {
  id: string;
  accountId: string;
  account?: Account | null;
  relationshipType: string | null;
  ownershipPercentage: string | null;
  appointedDate: string | null;
  ceasedDate: string | null;
  notes: string | null;
  source: 'account_contact' | 'account_people';
};

interface LinkedAccountsPanelProps {
  contact: Contact;
}

const RELATIONSHIP_TYPES = [
  { value: "director", label: "Director" },
  { value: "shareholder", label: "Shareholder" },
  { value: "beneficial_owner", label: "Beneficial Owner" },
  { value: "trustee", label: "Trustee" },
  { value: "partner", label: "Partner" },
  { value: "guarantor", label: "Guarantor" },
];

const getRelationshipBadgeClass = (type: string | null): string => {
  switch (type) {
    case "director":
    case "partner":
      return "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:border-blue-800";
    case "shareholder":
    case "beneficial_owner":
      return "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900 dark:text-emerald-300 dark:border-emerald-800";
    case "trustee":
      return "bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900 dark:text-violet-300 dark:border-violet-800";
    case "guarantor":
      return "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900 dark:text-amber-300 dark:border-amber-800";
    default:
      return "bg-secondary text-secondary-foreground";
  }
};

const getEntityTypeBadgeVariant = (type: string | null) => {
  switch (type) {
    case "company":
      return "default";
    case "trust":
      return "secondary";
    case "partnership":
      return "outline";
    case "sole_trader":
      return "secondary";
    default:
      return "secondary";
  }
};

export function LinkedAccountsPanel({ contact }: LinkedAccountsPanelProps) {
  const [, navigate] = useLocation();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isCreateAccountOpen, setIsCreateAccountOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [relationshipType, setRelationshipType] = useState<string>("");
  const [ownershipPercentage, setOwnershipPercentage] = useState<string>("");
  const [appointedDate, setAppointedDate] = useState<Date | undefined>();
  const [notes, setNotes] = useState<string>("");
  const [editingLink, setEditingLink] = useState<CombinedAccountLink | null>(null);
  const [editRelationshipType, setEditRelationshipType] = useState<string>("");
  const [editOwnershipPercentage, setEditOwnershipPercentage] = useState<string>("");
  const [editAppointedDate, setEditAppointedDate] = useState<Date | undefined>();
  const [editCeasedDate, setEditCeasedDate] = useState<Date | undefined>();
  const [editNotes, setEditNotes] = useState<string>("");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteSource, setDeleteSource] = useState<'account_contact' | 'account_people' | null>(null);
  const [showFilter, setShowFilter] = useState<'active' | 'all'>('active');
  const { toast } = useToast();

  // Fetch account_contacts relationships
  const { data: linkedAccountContacts = [], isLoading: isLoadingContacts } = useQuery<LinkedAccountWithDetails[]>({
    queryKey: ["/api/crm/account-contacts/contact", contact.id],
  });

  // Fetch account_people relationships (uses default queryFn with auth headers)
  const { data: linkedAccountPeople = [], isLoading: isLoadingPeople } = useQuery<AccountPeopleWithDetails[]>({
    queryKey: ["/api/crm/contacts", contact.id, "account-people"],
  });

  // Combine both sources into a unified list
  const linkedAccounts: CombinedAccountLink[] = [
    ...linkedAccountContacts.map(link => ({
      id: link.id,
      accountId: link.accountId,
      account: link.account,
      relationshipType: link.relationshipType,
      ownershipPercentage: link.ownershipPercentage,
      appointedDate: link.appointedDate,
      ceasedDate: link.ceasedDate,
      notes: link.notes,
      source: 'account_contact' as const,
    })),
    ...linkedAccountPeople.map(link => ({
      id: link.id,
      accountId: link.accountId,
      account: link.account,
      relationshipType: link.role, // role becomes relationshipType
      ownershipPercentage: null,
      appointedDate: link.appointmentDate,
      ceasedDate: link.ceasedDate,
      notes: link.notes,
      source: 'account_people' as const,
    })),
  ];

  const isLoading = isLoadingContacts || isLoadingPeople;

  const filteredAccounts = showFilter === 'active'
    ? linkedAccounts.filter(link => !link.ceasedDate)
    : linkedAccounts;

  const { data: accounts = [] } = useQuery<Account[]>({
    queryKey: ["/api/crm/accounts", { clientGroupId: contact.clientGroupId }],
    enabled: isAddDialogOpen,
  });

  const addMutation = useMutation({
    mutationFn: async (data: {
      accountId: string;
      contactId: string;
      relationshipType?: string;
      ownershipPercentage?: string;
      appointedDate?: string;
      notes?: string;
    }) => {
      await apiRequest("POST", `/api/crm/account-contacts`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/account-contacts/contact", contact.id] });
      toast({ title: "Account linked", description: "The account has been linked successfully." });
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to link account", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (data: { id: string; source: 'account_contact' | 'account_people' }) => {
      if (data.source === 'account_contact') {
        await apiRequest("DELETE", `/api/crm/account-contacts/${data.id}`);
      } else {
        // Delete from account_people - need to find the account ID first
        const peopleLink = linkedAccountPeople.find(l => l.id === data.id);
        if (peopleLink) {
          await apiRequest("DELETE", `/api/crm/accounts/${peopleLink.accountId}/people/${data.id}`);
        }
      }
    },
    onSuccess: () => {
      // Invalidate all related queries for bidirectional sync
      queryClient.invalidateQueries({ queryKey: ["/api/crm/account-contacts/contact", contact.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/contacts", contact.id, "account-people"] });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "/api/crm/account-contacts/account" ||
                            (query.queryKey[0] === "/api/crm/accounts" && query.queryKey[2] === "directors")
      });
      toast({ title: "Link removed", description: "The account link has been removed." });
      setDeleteId(null);
      setDeleteSource(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to remove link", variant: "destructive" });
    },
  });

  const editMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      relationshipType?: string;
      ownershipPercentage?: string | null;
      appointedDate?: string | null;
      ceasedDate?: string | null;
      notes?: string | null;
    }) => {
      const { id, ...body } = data;
      const response = await apiRequest("PATCH", `/api/crm/account-contacts/${id}`, body);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/account-contacts/contact", contact.id] });
      toast({ title: "Link updated", description: "The account link has been updated successfully." });
      handleCloseEditDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update link", variant: "destructive" });
    },
  });

  const handleOpenEditDialog = (link: CombinedAccountLink) => {
    setEditingLink(link);
    setEditRelationshipType(link.relationshipType || "");
    setEditOwnershipPercentage(link.ownershipPercentage || "");
    setEditAppointedDate(link.appointedDate ? new Date(link.appointedDate) : undefined);
    setEditCeasedDate(link.ceasedDate ? new Date(link.ceasedDate) : undefined);
    setEditNotes(link.notes || "");
  };

  const handleCloseEditDialog = () => {
    setEditingLink(null);
    setEditRelationshipType("");
    setEditOwnershipPercentage("");
    setEditAppointedDate(undefined);
    setEditCeasedDate(undefined);
    setEditNotes("");
  };

  const handleSaveEdit = () => {
    if (!editingLink) return;
    editMutation.mutate({
      id: editingLink.id,
      relationshipType: editRelationshipType || undefined,
      ownershipPercentage: editOwnershipPercentage?.trim() || null,
      appointedDate: editAppointedDate ? format(editAppointedDate, "yyyy-MM-dd") : null,
      ceasedDate: editCeasedDate ? format(editCeasedDate, "yyyy-MM-dd") : null,
      notes: editNotes || null,
    });
  };

  const handleCloseDialog = () => {
    setIsAddDialogOpen(false);
    setSelectedAccountId(null);
    setRelationshipType("");
    setOwnershipPercentage("");
    setAppointedDate(undefined);
    setNotes("");
  };

  const handleAdd = () => {
    if (!selectedAccountId) return;
    const parsedOwnership = ownershipPercentage && ownershipPercentage.trim() 
      ? ownershipPercentage.trim() 
      : undefined;
    addMutation.mutate({
      accountId: selectedAccountId,
      contactId: contact.id,
      relationshipType: relationshipType || undefined,
      ownershipPercentage: parsedOwnership,
      appointedDate: appointedDate ? format(appointedDate, "yyyy-MM-dd") : undefined,
      notes: notes || undefined,
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getAccountName = (link: CombinedAccountLink) => {
    if (link.account) {
      return link.account.legalName || "Unknown";
    }
    const account = accounts.find(a => a.id === link.accountId);
    if (account) {
      return account.legalName || "Unknown";
    }
    return "Unknown";
  };

  const getAccountEntityType = (link: CombinedAccountLink) => {
    if (link.account) {
      return link.account.entityType;
    }
    const account = accounts.find(a => a.id === link.accountId);
    if (account) {
      return account.entityType;
    }
    return null;
  };

  const formatRelationshipType = (type: string | null) => {
    if (!type) return "-";
    return type.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
  };

  const formatEntityType = (type: string | null) => {
    if (!type) return "-";
    return type.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="linked-accounts-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-muted-foreground">LINKED ACCOUNTS</h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsAddDialogOpen(true)}
          data-testid="button-add-linked-account"
        >
          <Plus className="h-3 w-3 mr-1" />
          Link Account
        </Button>
      </div>

      {linkedAccounts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center border rounded-md">
          <Link2 className="h-10 w-10 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No linked accounts yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click "Link Account" to associate an account with this contact
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1 mb-2">
            <button
              data-testid="filter-active"
              data-state={showFilter === 'active' ? 'active' : 'inactive'}
              onClick={() => setShowFilter('active')}
              className={cn(
                "px-2.5 py-1 text-xs font-medium rounded-md transition-colors",
                showFilter === 'active'
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
            >
              Active
            </button>
            <button
              data-testid="filter-all"
              data-state={showFilter === 'all' ? 'active' : 'inactive'}
              onClick={() => setShowFilter('all')}
              className={cn(
                "px-2.5 py-1 text-xs font-medium rounded-md transition-colors",
                showFilter === 'all'
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
            >
              All
            </button>
          </div>
          <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow className="h-10 bg-muted/50">
                <TableHead className="text-xs font-medium px-2 py-1">Account Name</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Entity Type</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Relationship</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Appointed Date</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Notes</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 w-[60px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAccounts.map((link) => (
                <TableRow
                  key={link.id}
                  className="h-10"
                  data-testid={`row-linked-account-${link.id}`}
                >
                  <TableCell className="px-2 py-1">
                    <button
                      onClick={() => navigate(`/accounts/${link.accountId}`)}
                      className="text-primary hover:underline text-sm"
                      data-testid={`link-account-${link.id}`}
                    >
                      {getAccountName(link)}
                    </button>
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    {getAccountEntityType(link) ? (
                      <Badge variant={getEntityTypeBadgeVariant(getAccountEntityType(link))}>
                        {formatEntityType(getAccountEntityType(link))}
                      </Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    <div>
                      {link.relationshipType ? (
                        <Badge variant="secondary" className={getRelationshipBadgeClass(link.relationshipType)}>
                          {formatRelationshipType(link.relationshipType)}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                      {showFilter === 'all' && link.ceasedDate && (
                        <div data-testid="ceased-date-inline" className="text-[11px] text-muted-foreground mt-0.5">
                          Ceased {formatDate(link.ceasedDate)}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {formatDate(link.appointedDate)}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm text-muted-foreground truncate max-w-[200px]">
                    {link.notes || "-"}
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenEditDialog(link)}
                        data-testid={`button-edit-linked-account-${link.id}`}
                        title="Edit link"
                        disabled={link.source === 'account_people'}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setDeleteId(link.id);
                          setDeleteSource(link.source);
                        }}
                        data-testid={`button-delete-linked-account-${link.id}`}
                        title="Remove link"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        </>
      )}

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link Account</DialogTitle>
            <DialogDescription>
              Select an account from the client group to link to this contact.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Account</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <AccountSelect
                    value={selectedAccountId}
                    onValueChange={(id) => {
                      setSelectedAccountId(id);
                      // Reset ownership/date when switching accounts
                      setOwnershipPercentage("");
                      setAppointedDate(undefined);
                    }}
                    clientGroupId={contact.clientGroupId}
                    placeholder="Select an account"
                    data-testid="select-linked-account"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setIsCreateAccountOpen(true)}
                  title="Create new account"
                  data-testid="button-create-new-account"
                >
                  <Building2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Relationship Type</Label>
              <Select value={relationshipType} onValueChange={setRelationshipType}>
                <SelectTrigger data-testid="select-relationship-type">
                  <SelectValue placeholder="Select relationship type" />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIP_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* #445: Hide ownership % and appointed date for trust accounts */}
            {accounts.find(a => a.id === selectedAccountId)?.entityType !== "trust" && (
              <>
                <div className="space-y-2">
                  <Label>Ownership Percentage</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="e.g., 25.00"
                    value={ownershipPercentage}
                    onChange={(e) => setOwnershipPercentage(e.target.value)}
                    data-testid="input-ownership-percentage"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Appointed Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !appointedDate && "text-muted-foreground"
                        )}
                        data-testid="input-appointed-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {appointedDate ? format(appointedDate, "d MMM yyyy") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={appointedDate}
                        onSelect={setAppointedDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Add any notes about this relationship..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="input-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={!selectedAccountId || addMutation.isPending}
              data-testid="button-confirm-link-account"
            >
              {addMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Link Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteId} onOpenChange={(open) => {
        if (!open) {
          setDeleteId(null);
          setDeleteSource(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Link</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this account link? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setDeleteId(null);
              setDeleteSource(null);
            }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (deleteId && deleteSource) {
                  deleteMutation.mutate({ id: deleteId, source: deleteSource });
                }
              }}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete-linked-account"
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AccountFormModal
        open={isCreateAccountOpen}
        onOpenChange={setIsCreateAccountOpen}
        defaultClientGroupId={contact.clientGroupId || undefined}
        onAccountCreated={(newAccount) => {
          setSelectedAccountId(newAccount.id);
          setIsCreateAccountOpen(false);
        }}
      />

      <Dialog open={!!editingLink} onOpenChange={(open) => { if (!open) handleCloseEditDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Linked Account</DialogTitle>
            <DialogDescription>
              Update the relationship details for {editingLink ? getAccountName(editingLink) : ""}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Relationship Type</Label>
              <Select value={editRelationshipType} onValueChange={setEditRelationshipType}>
                <SelectTrigger data-testid="edit-select-relationship-type">
                  <SelectValue placeholder="Select relationship type" />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIP_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Ownership Percentage</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                placeholder="e.g., 25.00"
                value={editOwnershipPercentage}
                onChange={(e) => setEditOwnershipPercentage(e.target.value)}
                data-testid="edit-input-ownership-percentage"
              />
            </div>
            <div className="space-y-2">
              <Label>Appointed Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !editAppointedDate && "text-muted-foreground"
                    )}
                    data-testid="edit-input-appointed-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {editAppointedDate ? format(editAppointedDate, "d MMM yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={editAppointedDate}
                    onSelect={setEditAppointedDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Ceased Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !editCeasedDate && "text-muted-foreground"
                    )}
                    data-testid="edit-input-ceased-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {editCeasedDate ? format(editCeasedDate, "d MMM yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={editCeasedDate}
                    onSelect={setEditCeasedDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Add any notes about this relationship..."
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                data-testid="edit-input-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEditDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={editMutation.isPending}
              data-testid="button-confirm-edit-linked-account"
            >
              {editMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
